// BRN-2123 AC3 — the host half of `chat:operator-viewport-context`: the state machine that decides
// WHETHER to post, WHEN, and with WHICH revision. Pure and injectable (clock, scheduler and the
// postMessage target are all parameters) so all four halves of the criterion are unit-provable
// without a DOM: exactly one message per real change, a strictly higher revision each time, the
// exact embed origin, and nothing at all before `chat:ready`.
//
// WHY A HOST STATE MACHINE AT ALL, rather than "post on every navigation". Three of the criterion's
// four clauses are properties of a SEQUENCE, not of a message: "exactly one" is a claim about a
// burst, "higher revision" is a claim about the previous send, and "after chat:ready" is a claim
// about the whole lifetime. None of them can be enforced at a call site that only knows about the
// current render.
//
// SECURITY REVIEW S1 IS WHY THE ORIGIN RULE HERE IS INERTNESS RATHER THAN A FALLBACK. A postMessage
// is authenticated by ORIGIN ALONE — there is no signature, nonce or capability on it — so the
// widget accepts a snapshot from anything speaking from its one discovered parent origin. This side
// therefore never widens its own aim: the target must be an EXACT origin (scheme + host + optional
// port, and nothing else), and a configured value that is a wildcard, a URL with a path, or empty
// makes this publisher post NOTHING. It never falls back to "*" — a wildcard target would broadcast
// the operator's viewport to whatever document happens to be framed.

import { findSurfaceForPathname } from './route-match';
import { composeSnapshotContent, snapshotIdentity, stampSnapshot } from './viewport-snapshot';
import type { ViewportSurface } from './surface-registry-types';
import type { ViewportViewModel } from './viewport-snapshot';

/** api-contract.md §1 — the message type, and the SDK's "debounce ≥ 250 ms" floor. */
export const OPERATOR_VIEWPORT_CONTEXT_TYPE = "chat:operator-viewport-context";
const BRIDGE_VERSION = 1;
const DEFAULT_DEBOUNCE_MS = 250;

/** The identity stored for "the host has cleared the view". A sentinel rather than an empty string,
 *  so a first-ever push on an undeclared page is correctly recognised as "nothing to clear". */
const CLEARED_IDENTITY = "\u0000cleared";
/** Nothing has been posted yet. Distinct from CLEARED so the very first push is never suppressed. */
const NOTHING_SENT_IDENTITY = "\u0000initial";

export interface ViewportPublisherTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface ViewportPublisherClock {
  now(): Date;
  schedule(callback: () => void, delayMs: number): unknown;
  cancel(handle: unknown): void;
}

export interface ViewportPublisherOptions {
  /** The exact embed origin. Anything that is not an exact origin makes this publisher inert. */
  readonly targetOrigin: string;
  readonly surfaces: readonly ViewportSurface[];
  /** The iframe's contentWindow, resolved lazily — it does not exist when the publisher is built. */
  readonly resolveTarget: () => ViewportPublisherTarget | null;
  readonly clock: ViewportPublisherClock;
  readonly debounceMs?: number;
}

export interface ViewportState {
  readonly pathname: string;
  readonly view: ViewportViewModel;
}

export interface ViewportPublisher {
  /** Called when the widget's `chat:ready` arrives. Releases the latest pending view, once. */
  markReady(): void;
  /** Called on every navigation, selection change or filter change. Debounced and deduplicated. */
  publish(state: ViewportState): void;
  dispose(): void;
}

/** A hostname made only of the characters a real host may carry — letters, digits, hyphens and
 *  dots, or a bracketed IPv6 literal. `URL` is deliberately permissive about the host and happily
 *  parses `https://*.example.com` into an `origin` equal to its input, so the round-trip check below
 *  is NOT sufficient on its own: measured, not assumed — the unit suite asserts that exact string. */
const CONCRETE_HOSTNAME = /^(\[[0-9a-f:.]+\]|[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*)$/i;

/**
 * An exact origin and nothing else: `https://host` or `https://host:8443`. A path, a query, a
 * trailing slash, a wildcard host or a non-web scheme all fail. This is the sending-side twin of the
 * widget's own origin lock, which likewise resolves a per-subdomain wildcard allowlist down to one
 * concrete origin before it will post anything.
 */
export function isExactOrigin(candidate: string): boolean {
  if (candidate.length === 0) {
    return false;
  }
  try {
    const url = new URL(candidate);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return false;
    }
    return url.origin === candidate && CONCRETE_HOSTNAME.test(url.hostname);
  } catch {
    return false;
  }
}

export function createViewportPublisher(options: ViewportPublisherOptions): ViewportPublisher {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const canPost = isExactOrigin(options.targetOrigin);

  let ready = false;
  let disposed = false;
  let pending: ViewportState | null = null;
  let timer: unknown = null;
  let revision = 0;
  let lastIdentity = NOTHING_SENT_IDENTITY;

  function cancelTimer(): void {
    if (timer !== null) {
      options.clock.cancel(timer);
      timer = null;
    }
  }

  function flush(): void {
    timer = null;
    if (disposed || !ready || !canPost || pending === null) {
      return;
    }
    const target = options.resolveTarget();
    if (target === null) {
      return; // the iframe is not mounted yet — the pending state stays pending
    }
    const state = pending;
    const surface = findSurfaceForPathname(options.surfaces, state.pathname);
    const content = surface === null ? null : composeSnapshotContent(surface, state.view, state.pathname);
    const identity = content === null ? CLEARED_IDENTITY : snapshotIdentity(content);
    if (identity === lastIdentity) {
      return; // not a change — a re-render, or a second undeclared page after an existing clear
    }
    if (content === null && lastIdentity === NOTHING_SENT_IDENTITY) {
      // Nothing has ever been grounded, so there is nothing to clear. Record the state so a later
      // arrival on a declared surface is still seen as a change.
      lastIdentity = CLEARED_IDENTITY;
      return;
    }
    revision += 1;
    lastIdentity = identity;
    const capturedAt = options.clock.now().toISOString();
    target.postMessage(
      {
        v: BRIDGE_VERSION,
        type: OPERATOR_VIEWPORT_CONTEXT_TYPE,
        revision,
        capturedAt,
        snapshot: content === null ? null : stampSnapshot(content, revision, capturedAt),
      },
      options.targetOrigin,
    );
  }

  return {
    markReady() {
      if (disposed || ready) {
        return;
      }
      ready = true;
      // The first push is immediate: the operator is already looking at a page when the chat opens,
      // and making them wait a debounce window for grounding they could have had at ready is a
      // latency cost with no benefit. Every LATER push goes through the debounce.
      cancelTimer();
      flush();
    },
    publish(state: ViewportState) {
      if (disposed) {
        return;
      }
      pending = state;
      cancelTimer();
      timer = options.clock.schedule(flush, debounceMs);
    },
    dispose() {
      disposed = true;
      cancelTimer();
      pending = null;
    },
  };
}
